import React from 'react';
import { 
  ArrowDown, 
  ArrowUp, 
  Wallet, 
  Buy, 
  Star,
  InfoCircle 
} from 'react-iconly';

// Style guide colors
const PRIMARY_BROWN = '#C97B53';
const NEUTRAL_BG = '#F9F9F9';
const NEUTRAL_WHITE = '#FFF';
const NEUTRAL_TEXT = '#222';
const ACCENT_GRAY = '#757575';
const SUCCESS_GREEN = '#4CAF50';
const WARNING_ORANGE = '#FF9800';
const ERROR_RED = '#F44336';

const headingFont = {
  fontFamily: 'Sora, sans-serif',
  fontWeight: 700,
};

const bodyFont = {
  fontFamily: 'Montserrat, sans-serif',
  fontWeight: 500,
};

/**
 * CancellationBreakdown Component
 * Displays detailed breakdown of cancellation fees and refunds
 * Used in seller, buyer, and admin dashboards
 */
export function CancellationBreakdown({ 
  refundBreakdown, 
  orderState, 
  initiatedBy, 
  cancelledAt, 
  cancellationReason,
  viewType = 'seller' // 'seller', 'buyer', 'admin'
}) {
  if (!refundBreakdown) {
    return null;
  }

  const {
    buyerRefund,
    stripeFeesLost,
    vestaFeeRefunded,
    sellerAbsorbsFees
  } = refundBreakdown;

  const getStateColor = (state) => {
    switch (state) {
      case 'pending': return WARNING_ORANGE;
      case 'accepted': return PRIMARY_BROWN;
      case 'in_progress': return WARNING_ORANGE;
      case 'completed': return SUCCESS_GREEN;
      case 'canceled': return ERROR_RED;
      default: return ACCENT_GRAY;
    }
  };

  const getStateLabel = (state) => {
    switch (state) {
      case 'pending': return 'Pending';
      case 'accepted': return 'Accepted';
      case 'in_progress': return 'In Progress';
      case 'completed': return 'Completed';
      case 'canceled': return 'Canceled';
      default: return state;
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getInitiatorLabel = (initiatedBy) => {
    switch (initiatedBy) {
      case 'buyer': return 'Customer';
      case 'seller': return 'Seller';
      case 'system': return 'System';
      default: return initiatedBy;
    }
  };

  return (
    <div style={{
      background: NEUTRAL_WHITE,
      borderRadius: 12,
      padding: 20,
      marginBottom: 16,
      border: '1px solid #E0E0E0',
      boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16,
        paddingBottom: 12,
        borderBottom: '1px solid #F0F0F0'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <InfoCircle set="regular" size={20} primaryColor={getStateColor(orderState)} />
          <h3 style={{
            ...headingFont,
            fontSize: 16,
            color: NEUTRAL_TEXT,
            margin: 0
          }}>
            Cancellation Details
          </h3>
        </div>
        <div style={{
          background: getStateColor(orderState),
          color: NEUTRAL_WHITE,
          padding: '4px 12px',
          borderRadius: 20,
          fontSize: 12,
          fontWeight: 600,
          ...bodyFont
        }}>
          {getStateLabel(orderState)}
        </div>
      </div>

      {/* Cancellation Info */}
      <div style={{ marginBottom: 16 }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 8
        }}>
          <span style={{ fontSize: 14, color: ACCENT_GRAY, ...bodyFont }}>
            Cancelled by:
          </span>
          <span style={{ fontSize: 14, color: NEUTRAL_TEXT, fontWeight: 600, ...bodyFont }}>
            {getInitiatorLabel(initiatedBy)}
          </span>
        </div>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 8
        }}>
          <span style={{ fontSize: 14, color: ACCENT_GRAY, ...bodyFont }}>
            Cancelled at:
          </span>
          <span style={{ fontSize: 14, color: NEUTRAL_TEXT, fontWeight: 600, ...bodyFont }}>
            {formatDate(cancelledAt)}
          </span>
        </div>
        {cancellationReason && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: 8
          }}>
            <span style={{ fontSize: 14, color: ACCENT_GRAY, ...bodyFont }}>
              Reason:
            </span>
            <span style={{ fontSize: 14, color: NEUTRAL_TEXT, fontWeight: 600, ...bodyFont }}>
              {cancellationReason}
            </span>
          </div>
        )}
      </div>

      {/* Financial Breakdown */}
      <div style={{
        background: '#F8F9FA',
        borderRadius: 8,
        padding: 16,
        marginBottom: 16
      }}>
        <h4 style={{
          ...headingFont,
          fontSize: 14,
          color: NEUTRAL_TEXT,
          margin: '0 0 12px 0'
        }}>
          Financial Breakdown
        </h4>

        {/* Customer Refund */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
          padding: '8px 0',
          borderBottom: '1px solid #E0E0E0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ArrowUp set="regular" size={16} primaryColor={SUCCESS_GREEN} />
            <span style={{ fontSize: 14, color: NEUTRAL_TEXT, ...bodyFont }}>
              Customer Refund
            </span>
          </div>
          <span style={{
            fontSize: 16,
            color: SUCCESS_GREEN,
            fontWeight: 700,
            ...bodyFont
          }}>
            ${buyerRefund.toFixed(2)}
          </span>
        </div>

        {/* Stripe Fees */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
          padding: '8px 0',
          borderBottom: '1px solid #E0E0E0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Buy set="regular" size={16} primaryColor={ERROR_RED} />
            <span style={{ fontSize: 14, color: NEUTRAL_TEXT, ...bodyFont }}>
              Stripe Processing Fees
            </span>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{
              fontSize: 16,
              color: ERROR_RED,
              fontWeight: 700,
              ...bodyFont
            }}>
              ${stripeFeesLost.toFixed(2)}
            </div>
            <div style={{
              fontSize: 12,
              color: ACCENT_GRAY,
              ...bodyFont
            }}>
              {sellerAbsorbsFees ? 'Seller absorbs' : 'Not charged'}
            </div>
          </div>
        </div>

        {/* Vesta Fee */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
          padding: '8px 0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Star set="regular" size={16} primaryColor={PRIMARY_BROWN} />
            <span style={{ fontSize: 14, color: NEUTRAL_TEXT, ...bodyFont }}>
              Vesta Service Fee
            </span>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{
              fontSize: 16,
              color: vestaFeeRefunded > 0 ? SUCCESS_GREEN : PRIMARY_BROWN,
              fontWeight: 700,
              ...bodyFont
            }}>
              ${vestaFeeRefunded.toFixed(2)}
            </div>
            <div style={{
              fontSize: 12,
              color: ACCENT_GRAY,
              ...bodyFont
            }}>
              {vestaFeeRefunded > 0 ? 'Refunded to seller' : 'Kept by Vesta'}
            </div>
          </div>
        </div>
      </div>

      {/* Summary based on view type */}
      {viewType === 'seller' && (
        <div style={{
          background: sellerAbsorbsFees ? '#FFF3E0' : '#E8F5E8',
          borderRadius: 8,
          padding: 12,
          border: `1px solid ${sellerAbsorbsFees ? WARNING_ORANGE : SUCCESS_GREEN}`
        }}>
          <div style={{
            fontSize: 14,
            color: sellerAbsorbsFees ? WARNING_ORANGE : SUCCESS_GREEN,
            fontWeight: 600,
            ...bodyFont
          }}>
            {sellerAbsorbsFees 
              ? `You absorbed $${stripeFeesLost.toFixed(2)} in processing fees`
              : 'No fees were charged for this cancellation'
            }
          </div>
        </div>
      )}

      {viewType === 'buyer' && (
        <div style={{
          background: '#E8F5E8',
          borderRadius: 8,
          padding: 12,
          border: `1px solid ${SUCCESS_GREEN}`
        }}>
          <div style={{
            fontSize: 14,
            color: SUCCESS_GREEN,
            fontWeight: 600,
            ...bodyFont
          }}>
            You received a full refund of ${buyerRefund.toFixed(2)}
          </div>
        </div>
      )}

      {viewType === 'admin' && (
        <div style={{
          background: '#F0F8FF',
          borderRadius: 8,
          padding: 12,
          border: '1px solid #2196F3'
        }}>
          <div style={{
            fontSize: 14,
            color: '#2196F3',
            fontWeight: 600,
            ...bodyFont
          }}>
            Platform Impact: {vestaFeeRefunded > 0 ? `Refunded $${vestaFeeRefunded.toFixed(2)}` : `Kept $${(2.00 - vestaFeeRefunded).toFixed(2)}`}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * CancellationSummary Component
 * Compact view for order lists
 */
export function CancellationSummary({ 
  refundBreakdown, 
  orderState, 
  initiatedBy,
  viewType = 'seller'
}) {
  if (!refundBreakdown) return null;

  const { buyerRefund, stripeFeesLost, vestaFeeRefunded, sellerAbsorbsFees } = refundBreakdown;

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      fontSize: 12,
      color: ACCENT_GRAY,
      ...bodyFont
    }}>
      <ArrowDown set="regular" size={14} primaryColor={ERROR_RED} />
      <span>
        {viewType === 'seller' && (
          sellerAbsorbsFees 
            ? `Lost $${stripeFeesLost.toFixed(2)} in fees`
            : 'No fees charged'
        )}
        {viewType === 'buyer' && (
          `Refunded $${buyerRefund.toFixed(2)}`
        )}
        {viewType === 'admin' && (
          vestaFeeRefunded > 0 
            ? `Refunded $${vestaFeeRefunded.toFixed(2)}`
            : `Kept $${(2.00 - vestaFeeRefunded).toFixed(2)}`
        )}
      </span>
    </div>
  );
}
